/**
 * @email       : saraman@ibl.mx
 * @author      : Arman Ali
 * @title       : Full Stack Developer
 * @github      : https://github.com/Arman-Arzoo
 *
 * @component   : Approval
 * @description : This component manages the Approval system. It handles CRUD operations,
 *                pagination, search functionality, and selection logic for bulk actions.
 *                It provides a dynamic table interface for managing approval records.
 *
 * @renders     : - A searchable and interactive approval table
 *                - Add and delete buttons (based on user permissions)
 *                - Confirmation modal for deletions
 *
 * @features    : - Custom table with checkbox selection
 *                - Search input
 *                - Role-based permission checks
 *                - Modularized actions and table handling
 */

import { permissionObj } from '../../Helpers/permission';
import routes from '../../Routes/routes';
import HeadTittle from '../../components/HeadTittle';
import NotInformation from '../../components/NotInformation';
import SearchSimple from '../../components/SearchSimples/SearchSimple';
import ButtonAction from '../../components/buttons/ButtonAction';
import {AddIcon, TrashOutlineIcon} from '../../components/buttons/IconButtons';
import CustomExpandableTable from '../../components/table/CustomExpandableTable';
import ApprovalApp from './ApprovalApp';
import React from "react";
import DeleteModal from "../Modals/DeleteModal";

const Approval = () => {
    const {
        t,
        deleteModal,
        setDeleteModal,
        DEV,
        permission,
        selectedList,
        columns,
        results,
        setResults,
        searchElement,
        setSearchElement,
        setSearch,
        option,
        searchId,
        setSelectedList,
        rowsPerPage,
        simpleSearch,
        setSimpleSearch,
        setBodyForExport,
        contractorId,
        supplierId,
        pagination,
        deleteApprovalFn,
        handleCheckboxChange,
        handleDeleteAll,
        renderOptionsMenu,
        handleRowExpand,
        renderExpandedRow
    } = ApprovalApp();


    return (
        <>
            <HeadTittle name={'APPROVAL_FLOW'}>
                {(DEV || permission?.includes(permissionObj?.WEB_APPROVAL_FLOW_CREATE)) && (
                    <ButtonAction
                        id='btn_a_create'
                        name='create'
                        className='btn-icon-add'
                        Icon={AddIcon}
                        url={routes.approval.create}
                    />
                )}
                {(DEV || permission?.includes(permissionObj?.WEB_APPROVAL_FLOW_DELETE)) && (
                    <ButtonAction
                        id='btn_a_delete'
                        name='delete'
                        className='btn-icon-delete'
                        disabled={selectedList?.length === 0}
                        Icon={TrashOutlineIcon}
                        onClick={() => setDeleteModal(true)}
                    />
                )}
            </HeadTittle>

            <SearchSimple
                id={searchId}
                setBodyForExport={setBodyForExport}
                setSearchResults={setResults}
                searchResults={results}
                searchElement={searchElement}
                setSearchElement={setSearchElement}
                optionTable={option}
                rowsPerPage={rowsPerPage}
                setSearch={setSearch}
                simpleSearch={simpleSearch}
                setSimpleSearch={setSimpleSearch}
                contractorId={contractorId}
                supplierId={supplierId}
            />

            {results?.content?.length > 0 ? (
                <>
                    <CustomExpandableTable
                        columns={columns}
                        rows={results?.content || []}
                        renderExpandedRow={renderExpandedRow}
                        renderOptionsMenu={(DEV ||
                            permission.includes(permissionObj?.WEB_APPROVAL_FLOW_UPDATE) ||
                            permission.includes(permissionObj?.WEB_APPROVAL_FLOW_DELETE)) ? renderOptionsMenu : false
                        }
                        onRowExpand={handleRowExpand}
                        selectedList={selectedList}
                        handleCheckboxChange={handleCheckboxChange}
                        handleDeleteAll={handleDeleteAll}
                    />
                    {pagination()}
                </>
            ) : (
                <NotInformation
                    card={true}
                    text={typeof results?.content === "undefined" ? t('loading') : t('no_approval_flows')}
                    showButton={((DEV || permission?.includes(permissionObj?.WEB_APPROVAL_FLOW_CREATE)) && results?.content?.length === 0)}
                    buttonId={'btn_a_link_create'}
                    buttonUrl={routes.approval.create}
                    buttonTrans={'create_new_approval_flow'}
                    searchElement={searchElement !== ''}
                />
            )}

            <DeleteModal
                show={deleteModal}
                onHide={() => setDeleteModal(false)}
                data={selectedList}
                onClickFn={() => {
                    deleteApprovalFn(selectedList);
                    setDeleteModal(false);
                }}
                title_modal={'delete_approvals'}
                element_modal={'APPROVAL_FLOW'}
                isReset={() => setSelectedList([])}
            />
        </>
    );
};

export default Approval;
